import TableComponent from "../ui/Table";
import { urls, toastify_status } from "../config/variables";
import Toastify from "../ui/Toastify";
import { useState, useEffect, useContext, useMemo } from "react";
import useAxios from "../../hooks/useAxios";
import AuthContext from "../../contexts/auth-context";
import AppContext from "../../contexts/app-context";
import {
  Box,
  TextField,
  Pagination,
  Alert,
  CircularProgress,
  InputAdornment,
  Typography,
} from "@mui/material";
import { ArrowDropUp, ArrowDropDown, Search as SearchIcon } from "@mui/icons-material";
import DangerousIcon from "@mui/icons-material/Dangerous";
import InfoIcon from "@mui/icons-material/Info";
import { motion } from "framer-motion";
import EncounterCard from "../users/EncounterCard/EncounterCard";
import { validateEncountersList } from "../../Utils/validateHttpResponse";
import {
  Info,
} from "@mui/icons-material";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import Divider from "@mui/material/Divider"

const CptIssuesTable = () => {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const globalFilterQuery = appCtx?.globalFilterQuery || "";
  const token = authCtx.token;
  const { operation } = useAxios();

  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [tableDataError, setTableDataError] = useState(undefined);
  const [isFlipped, setIsFlipped] = useState(false);

  // issue encounters
  const [issueEncounterIds, setIssueEncounterIds] = useState([])
  const [patientEncounters, setPatientEncounters] = useState([]);
  const [patientEncounterCards, setPatientEncounterCards] = useState([]);

  const [loadingEncounters, setLoadingEncounters] = useState(true)
  const [issueEncountersError, setIssueEncountersError] = useState(true)
  const [count, setCount] = useState(1);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5


  const GetTableData = () => {
    setLoadingData(true);
    setTableDataError(undefined);

    const globalParams = new URLSearchParams(globalFilterQuery);
    const foundFromDate = globalParams.get("from_date");
    const foundToDate = globalParams.get("to_date");
    const tableParams = new URLSearchParams();

    if (foundFromDate) tableParams.set("from_date", foundFromDate);
    if (foundToDate) tableParams.set("to_date", foundToDate);

    operation(
      {
        method: "GET",
        url: urls.CPT_ISSUES_TABLE + "?" + tableParams,
        headers: { Authorization: `token ${token}` },
      },
      handleSetTableData,
      handleSetTableDataError
    );
  };

  const handleSetTableData = (response) => {
    setLoadingData(false);

    // Reformat the data for consistent structure
    const normalizedData =
      response?.map((item) => {
        const validIssues = item.issue_details?.filter((issue) => issue.count > 0) || [];
        const totalIssues = validIssues.reduce((sum, issue) => sum + issue.count, 0);

        return {
          encounter_ids: item.encounter_ids || [],
          // cpt_code: item.cpt_code || "",
          // cpt_description: item.cpt_description || "",
          cpt: {cptCode: item.cpt_code || "", cptDescription: item.cpt_description || ""},
          issue_cost: item.issue_cost || "",
          total_issues: totalIssues,
          issue_details: validIssues.map((issue) => ({
            label: issue.label,
            count: issue.count,
          })),
        };
      }) || [];

    setTableData(normalizedData);
  };

  const handleSetTableDataError = (error) => {
    setLoadingData(false);
    setTableDataError(error);
  };

  useEffect(() => {
    GetTableData();
    // eslint-disable-next-line
  }, []);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const handleChangeCurrentPage = (e, v) => setCurrentPage(v);

  // Search + Sorting
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("cpt_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  // Filter + Sort
  const filteredData = useMemo(() => {
    let filtered = tableData.filter((item) => {
      const search = searchValue.toLowerCase();

      // Convert issue_details into a searchable string (like "1 Diagnosis not related, 2 Therapeutic duplication")
      const detailsString = item.issue_details
        ?.map((d) => `${d.count} ${d.label}`)
        .join(" ")
        .toLowerCase();

        // Check if any field matches search value
      return (
        // item.cpt_code?.toLowerCase().includes(search) ||
        // item.cpt_description?.toLowerCase().includes(search) ||
        item.cpt?.cptDescription?.toLowerCase().includes(search) ||
        item.cpt?.cptCode?.toLowerCase().includes(search) ||
        item.issue_cost?.toLowerCase().includes(search) ||
        item.total_issues?.toString().includes(search) ||
        detailsString?.includes(search)
      );
    });

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // handle issue_details sorting by total count or first label alphabetically
      if (sortBy === "issue_details") {
        const aString = a.issue_details.map((d) => d.label).join(", ");
        const bString = b.issue_details.map((d) => d.label).join(", ");
        return sortDirection === "asc"
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      }

      // numeric sort
      if (!isNaN(aVal) && !isNaN(bVal)) {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      // string sort
      if (aVal && bVal) {
        return sortDirection === "asc"
          ? aVal.toString().localeCompare(bVal.toString())
          : bVal.toString().localeCompare(aVal.toString());
      }

      return 0;
    });

    return filtered;
  }, [tableData, searchValue, sortBy, sortDirection]);

  
  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  // Table Headers 
  const tableHeaders = [
    // { label: "CPT Code", key: "cpt_code" },
    // { label: "Description", key: "cpt_description" },
    { label: "CPT", key: "cpt" },
    { label: "Cost", key: "issue_cost" },
    { label: "Issues", key: "total_issues" },
    { label: "Details", key: "issue_details" },
    { label: "Encounters", key: "encounter_ids" },
  ];

  const handleFlip = (ids) => {
    setIsFlipped(true)
    setIssueEncounterIds(ids)
  }

  const handleFlipBack = () => {
    setIsFlipped(false)
    setIssueEncounterIds([])
    setPage(1)
  }

  const sortByDate = (array) => {
    return array.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  };

  const storePatientEncounterCards = (encounters) => {
    if (typeof encounters !== 'undefined') {
      return encounters.map((patientEncounter, index) => {
        return <EncounterCard key={index} encounter={patientEncounter} />;
      });
    }
  }

  const getIssueEncountersByIDs = () => {
    setLoadingEncounters(true)
    setIssueEncountersError(false)
    operation(
      {
        method: 'GET',
        url: `${urls.LIST_ENCOUNTERS}?encounter_ids=${issueEncounterIds}&page=${page}&page_size=${PAGE_SIZE}`,

        headers: { Authorization: `token ${token}` },
      },
      setFilteredIssueEncounters,
      (error) => {
        console.log(error)
        Toastify({message: 'Something went wrong while fetching the issue encounters. Please try again later.', state: toastify_status.ERROR})
      }
    );
  };

  useEffect(() => {
    if (isFlipped && issueEncounterIds.length > 0) {
      getIssueEncountersByIDs()
    }
    // eslint-disable-next-line
  }, [isFlipped, issueEncounterIds, page])

  const setFilteredIssueEncounters = (encounters) => {
    setLoadingEncounters(false)
    if (validateEncountersList(encounters)) {
      const sortedPatientEncounters = sortByDate(encounters.results);
      setPatientEncounters(sortedPatientEncounters);
      setPatientEncounterCards(storePatientEncounterCards(sortedPatientEncounters))
      setCount(Math.ceil(encounters.count / PAGE_SIZE));
    } else {
      console.log('invlid encounters list response')
      Toastify({ message: 'invlid encounters list response', state: toastify_status.ERROR })
    }
  };

  return (
    <Box sx={{
      perspective: "1000px",
      height: isFlipped ? "560px" : "390px",
      position: 'relative',
      transition: 'height 0.4s ease-in-out',
      overflow: "hidden"
    }}>
      <motion.div
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          transformStyle: "preserve-3d",
        }}
      >
        {/* FRONT SIDE */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            borderRadius: 2,
            backgroundColor: "white",
            transform: "translateZ(1px)",
            pointerEvents: isFlipped ? "none" : "auto",
            zIndex: isFlipped ? 0 : 2,
          }}
        >
          {/* Your Search/Table Content */}
          <Box sx={{ p: 1 }}>
            {/* Search */}
            <Box
              sx={{
                alignItems: "center",
                marginBottom: "10px",
                justifyContent: "space-between",
                display: 'flex',
              }}
            >
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setCurrentPage(1);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Pagination */}
              {tableData.length > 5 && (
                <Box sx={{ marginRight: "10px" }}>
                  <Pagination
                    page={currentPage}
                    count={totalPages}
                    onChange={handleChangeCurrentPage}
                    color="primary"
                  />
                </Box>
              )}
            </Box>

            {/* Conditional Rendering */}
            {!tableDataError && tableData.length === 0 && !loadingData ? (
              <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                No data available.
              </Alert>
            ) : tableDataError ? (
              <Alert icon={<DangerousIcon fontSize="inherit" />} severity="error">
                Something went wrong.
              </Alert>
            ) : loadingData ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Table */}
                <TableComponent
                  stickyHeader={!isFlipped}
                  tableHead={tableHeaders.map((h) => (
                    <Box
                      key={h.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => handleSort(h.key)}
                    >
                      {h.label}
                      {sortBy === h.key &&
                        (sortDirection === "asc" ? (
                          <ArrowDropUp fontSize="small" />
                        ) : (
                          <ArrowDropDown fontSize="small" />
                        ))}
                    </Box>
                  ))}
                  tableBody={currentRows.map((row) => {
                    const validIssues =
                      row.issue_details?.filter((issue) => issue.count > 0) || [];
                    const totalIssues = validIssues.reduce(
                      (sum, issue) => sum + issue.count,
                      0
                    );

                    return {
                      cpt: (
                        <Box sx={{textAlign: 'left', pl: 1}}>
                            <Typography
                              sx={{
                                padding: "2px 6px",
                                borderRadius: '4px',
                                fontWeight: "500",
                                fontSize: "15px"
                              }}
                            >
                              {row.cpt?.cptCode} - {row.cpt?.cptDescription}
                            </Typography>
                        </Box>
                      ),
                      issue_cost: row.issue_cost,
                      total_issues: `${totalIssues}`,
                      issue_details: (
                        <Box sx={{ pl: 1,  }}>
                          {validIssues.map((issue) => {
                            return (
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}} key={issue.label}>
                              <Typography
                              key={issue.label}
                              sx={{
                                fontWeight: "500",
                                fontSize: "14px",
                                textAlign: "left",
                                mt: 0.5,
                                mb: 0.5,
                                width: '90%',
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              {issue.label}
                              </Typography>
                              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                              <Box
                                component="span"
                                sx={{
                                  backgroundColor: "#E14242",
                                  color: "#fff",
                                  minWidth: "20px",
                                  minHeight: "20px",
                                  p: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  display: "inline-flex",
                                  justifyContent: "center",
                                  alignItems: "center"
                                }}
                              >
                                {issue.count}
                              </Box>
                            </Box>
                          )
                          })}
                        </Box>
                      ),
                      encounter_ids: (
                        <Typography
                        sx={{
                                width: "fit-content",
                                margin: "0 auto",
                                padding: "2px 18px",
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                backgroundColor: '#E14242',
                                color: '#fff',
                                fontWeight: "500",
                                '&:hover': {
                                  backgroundColor: '#C13838',
                                },
                              }}
                          onClick={() => handleFlip(row.encounter_ids)}
                        >
                          Show
                        </Typography>
                      ),
                    };
                  })}
                  bodyCells={[
                    // "cpt_code",
                    // "cpt_description",
                    "cpt",
                    "issue_cost",
                    "total_issues",
                    "issue_details",
                    "encounter_ids",
                  ]}
                  bodyCellsCustomSymbol={{ issue_cost: "$" }}
                  customStyle={{
                    tableContainer: { maxHeight: "330px", overflowY: "auto" },
                    headerTableCell: (cell) => ({
                      textAlign: "center",
                      border: "1px solid #d9d9d9",
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#000000c7",
                      backgroundColor: "#F5F5F5",
                      p: 1,
                      width:
                        cell.key === "issue_cost"
                          ? "10%"
                          : cell.key === "total_issues"
                            ? "8%"
                            : cell.key === "cpt_code"
                              ? "12%"
                              : cell.key === "encounter_ids"
                                ? "12%"
                                : cell.key === "cpt_description"
                                  ? "23%"
                                    : cell.key === "cpt"
                                      ? "34%"
                                         : "36%",
                    }),
                    row: {
                      border: "1px solid #d9d9d9",
                      transition: "0.2s",
                      backgroundColor: "#FCFEFE",
                      "&:hover": { backgroundColor: "#f9f9f9" },
                    },
                    tableCell: {
                      textAlign: "center",
                      border: "1px solid #d9d9d9",
                      fontWeight: "500",
                      verticalAlign: "middle",
                      padding: 1,
                      fontSize: "16px",
                    },
                  }}
                />
              </>
            )}
          </Box>
        </Box>

        {/* BACK SIDE */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg) translateZ(1px)",
            borderRadius: 2,
            backgroundColor: "white",
            overflow: "hidden",
            pointerEvents: isFlipped ? "auto" : "none",
            zIndex: isFlipped ? 2 : 0,
          }}
        >
          <Box sx={{ p: 1, height: '100%', width: '100%', overflow: 'scroll' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px' }}>
                <KeyboardBackspaceIcon
                  sx={{ fontSize: "40px", color: "#8ED5C8", cursor: "pointer", textDecoration: "underline", fontWeight: '500' }}
                  onClick={handleFlipBack}
                />
                {count > 1 && <Box>
                  <Pagination
                    page={page}
                    count={count}
                    onChange={(e, v) => setPage(v)}
                    color="primary"
                    size="small"
                  />
                </Box>}
              </Box>

              <Box>
                <Box>
                  {loadingEncounters
                    ? <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <CircularProgress size="60px" sx={{}} />
                    </Box>
                    : !loadingEncounters && issueEncountersError
                      ? <Box sx={{ margin: '10px' }}>
                        <Alert icon={<DangerousIcon />} severity="error">
                          Something went wrong.
                        </Alert>
                      </Box>
                      : patientEncounters.length === 0
                        ? <Box sx={{ margin: '10px' }}>
                          <Alert icon={<Info />} severity="info">
                            No encounters available for this state
                          </Alert>
                        </Box>
                        : <Box sx={{ margin: '20px 0', padding: '0 10px' }}>
                          {patientEncounterCards}
                        </Box>}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default CptIssuesTable;
